import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getPublicPlaybooks } from '../data/mockPlaybooksV2';
import { initializeMockApi } from '../utils/api';
import { formatReturnsCentered, returnsToCentered } from '../utils/returns';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BarChartIcon from '@mui/icons-material/BarChart';

// Only use mock data in development and if explicitly enabled
const USE_MOCK_DATA = process.env.NODE_ENV !== 'production' 
  && process.env.REACT_APP_USE_MOCK_DATA === 'true';

const STAT_MODES = [
  { value: 'mean', label: 'Average', short: 'Avg' },
  { value: 'median', label: 'Median', short: 'Med' }
];

// A playbook's per-event results for one field ('returns' or 'accuracy'), without gaps.
function byEvent(playbook, field) {
  const values = playbook[`${field}_by_event`];
  return Array.isArray(values) ? values.filter(v => typeof v === 'number' && Number.isFinite(v)) : [];
}

function median(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Accuracy or return across a playbook's past events, as the average or the median.
// The average is the stored figure, as before; a playbook without per-event history has no median.
function eventStat(playbook, field, mode) {
  const values = byEvent(playbook, field);
  if (mode === 'median') return values.length ? median(values) : null;
  const stored = playbook[`avg_${field}`];
  if (typeof stored === 'number') return stored;
  return values.length ? values.reduce((sum, v) => sum + v, 0) / values.length : null;
}

// Share of past events that ended above break-even (a return multiple over 1.0).
function profitChance(playbook) {
  const returns = byEvent(playbook, 'returns');
  if (!returns.length) return null;
  const profitable = returns.filter(r => r > 1).length;
  return { share: profitable / returns.length, profitable, events: returns.length };
}

// A custom playbook has a record only once the daily job tracks it (tracking_started). Older
// documents can carry history fields from a migration; those are not its record.
const RECORD_FIELDS = ['returns_by_event', 'accuracy_by_event', 'included_event_dates', 'distributions_summary',
  'last_event_return', 'accuracy_pct_last_event', 'avg_returns', 'avg_accuracy', 'current_win_streak',
  'longest_win_streak', 'longest_loss_streak'];
function withRecordsOnly(list) {
  return (list || []).map((playbook) => {
    if (playbook.is_prebuilt !== false || playbook.tracking_started) return playbook;
    const cleaned = { ...playbook };
    RECORD_FIELDS.forEach((field) => { delete cleaned[field]; });
    return cleaned;
  });
}

export default function TopPlaybooks() {
  const navigate = useNavigate();
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'odikai', 'user'
  const [sortBy, setSortBy] = useState('last_event_return'); // 'last_event_return', 'avg_accuracy', 'avg_returns', 'last_event_accuracy', 'current_win_streak', 'name'
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc', 'desc'
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [statMode, setStatMode] = useState('mean'); // 'mean', 'median'
  const sortDropdownRef = useRef(null);

  const currentStatMode = STAT_MODES.find(m => m.value === statMode);

  const SORT_OPTIONS = [
    { value: 'last_event_return', label: 'Last event return' },
    { value: 'avg_returns', label: `${currentStatMode.label} return` },
    { value: 'avg_accuracy', label: `${currentStatMode.label} accuracy` },
    { value: 'profit_chance', label: 'Chance of profit' },
    { value: 'last_event_accuracy', label: 'Last event accuracy' },
    { value: 'current_win_streak', label: 'Win streak' },
    { value: 'name', label: 'Title' }
  ];

  useEffect(() => {
    async function fetchPlaybooks() {
      setLoading(true);
      try {
        if (USE_MOCK_DATA) {
          // Use mock data during parallel development
          const mockData = getPublicPlaybooks();
          // Initialize mock API with all playbooks
          initializeMockApi(mockData);
          setPlaybooks(withRecordsOnly(mockData));
        } else {
          // Query Firestore for public playbooks from unified collection
          const playbooksRef = collection(db, 'playbooks');
          
          try {
            // Query with filters that match security rules
            // We need separate queries for public user playbooks and prebuilt playbooks
            // Query 1: Public user playbooks
            // Query 2: All prebuilt playbooks (they're always readable per rules)
            const [publicUserSnapshot, prebuiltSnapshot] = await Promise.all([
              getDocs(query(playbooksRef, where('is_public', '==', true))).catch(() => {
                return { size: 0, forEach: () => {} };
              }),
              getDocs(query(playbooksRef, where('is_prebuilt', '==', true))).catch(() => {
                // If index is missing, return empty - we'll only show public user playbooks
                return { size: 0, forEach: () => {} };
              })
            ]);
            
            // Combine results
            const allPlaybooks = [];
            const prebuiltPlaybooksByType = new Map();
            
            // Process public user playbooks (exclude prebuilt to avoid duplicates)
            publicUserSnapshot.forEach((doc) => {
              const data = doc.data();
              if (data.is_prebuilt !== true) {
                allPlaybooks.push({ 
                  id: doc.id, 
                  ...data,
                  creator_type: data.creator_type || 'user' // Ensure creator_type is set
                });
              }
            });
            
            // Process prebuilt playbooks
            prebuiltSnapshot.forEach((doc) => {
              const data = doc.data();
              // Playbooks the admin has hidden are left out
              if (data.is_public === false) return;
              if (data.type) {
                const playbookType = data.type;
                const eventDate = data.event?.date ? new Date(data.event.date) : 
                                (data.last_updated ? (data.last_updated.toDate ? data.last_updated.toDate() : new Date(data.last_updated)) : new Date(0));
                
                // Keep only the most recent playbook for each type
                if (!prebuiltPlaybooksByType.has(playbookType) || 
                    eventDate > (prebuiltPlaybooksByType.get(playbookType).eventDate || new Date(0))) {
                  prebuiltPlaybooksByType.set(playbookType, {
                    id: doc.id,
                    data: data,
                    eventDate: eventDate
                  });
                }
              } else {
                // Prebuilt without type - add it anyway
                allPlaybooks.push({
                  id: doc.id,
                  ...data,
                  creator_type: data.creator_type || 'odikai',
                  creator_name: data.creator_name || 'Odikai',
                  is_public: true,
                  name: data.name || 'Prebuilt Playbook'
                });
              }
            });
            
            // Add the most recent prebuilt playbook for each type
            prebuiltPlaybooksByType.forEach(({ id, data }) => {
              allPlaybooks.push({
                id: id,
                ...data,
                creator_type: data.creator_type || 'odikai',
                creator_name: data.creator_name || 'Odikai',
                is_public: true,
                name: data.name || `${(data.type || 'Prebuilt').charAt(0).toUpperCase() + (data.type || 'Prebuilt').slice(1)} Playbook`,
                last_updated: data.last_updated ? (data.last_updated.toDate ? data.last_updated.toDate().toISOString() : data.last_updated) : null
              });
            });
            
            setPlaybooks(withRecordsOnly(allPlaybooks));
          } catch (queryError) {
            // If queries fail, try a simpler approach - just get all public playbooks
            try {
              const simpleSnapshot = await getDocs(query(playbooksRef, where('is_public', '==', true)));
              const simplePlaybooks = [];
              simpleSnapshot.forEach((doc) => {
                simplePlaybooks.push({ id: doc.id, ...doc.data() });
              });
              setPlaybooks(withRecordsOnly(simplePlaybooks));
            } catch (fallbackError) {
              setPlaybooks([]);
            }
          }
        }
      } catch (error) {
        // Silently handle errors
      } finally {
        setLoading(false);
      }
    }

    fetchPlaybooks();
  }, []);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) {
        setSortDropdownOpen(false);
      }
    };
    if (sortDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortDropdownOpen]);

  // Filter and sort playbooks
  const filteredAndSortedPlaybooks = React.useMemo(() => {
    let filtered = playbooks;

    // Apply creator type filter
    if (filter !== 'all') {
      filtered = filtered.filter(pb => {
        // Normalize creator_type - treat undefined/null as 'user' for filtering
        const creatorType = pb.creator_type || 'user';
        return creatorType === filter;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'last_event_return':
          aValue = returnsToCentered(a.last_event_return) ?? -Infinity;
          bValue = returnsToCentered(b.last_event_return) ?? -Infinity;
          break;
        case 'avg_accuracy':
          aValue = eventStat(a, 'accuracy', statMode) ?? -Infinity;
          bValue = eventStat(b, 'accuracy', statMode) ?? -Infinity;
          break;
        case 'avg_returns':
          aValue = returnsToCentered(eventStat(a, 'returns', statMode)) ?? -Infinity;
          bValue = returnsToCentered(eventStat(b, 'returns', statMode)) ?? -Infinity;
          break;
        case 'profit_chance':
          aValue = profitChance(a)?.share ?? -Infinity;
          bValue = profitChance(b)?.share ?? -Infinity;
          break;
        case 'last_event_accuracy':
          aValue = a.accuracy_pct_last_event || 0;
          bValue = b.accuracy_pct_last_event || 0;
          break;
        case 'current_win_streak':
          aValue = a.current_win_streak ?? -1;
          bValue = b.current_win_streak ?? -1;
          break;
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return sorted;
  }, [playbooks, filter, sortBy, sortDirection, statMode]);

  // A missing figure is not a bad one: it shows as a grey dash, not a red N/A
  // (custom playbooks have no results until they are tracked).
  const formatPercent = (value) => {
    if (value === undefined || value === null) return '—';
    return `${(value * 100).toFixed(2)}%`;
  };
  const formatReturn = (raw) => (returnsToCentered(raw) === null ? '—' : formatReturnsCentered(raw));
  const toneAt = (value, threshold) => (
    typeof value === 'number' && !Number.isNaN(value) ? (value >= threshold ? 'positive' : 'negative') : 'muted'
  );
  const untracked = (playbook, accuracy, returns) => (
    accuracy == null && returns == null && playbook.last_event_return == null && playbook.accuracy_pct_last_event == null
  );

  const formatChance = (chance) => (chance ? `${Math.round(chance.share * 100)}%` : '—');
  const chanceDetail = (chance) => (chance ? `${chance.profitable} of ${chance.events} events made money` : undefined);

  const handleViewPlaybook = (playbookId) => {
    navigate(`/playbooks/${playbookId}`);
  };

  const handleViewPicks = (playbookId) => {
    // Extract playbook type from ID (e.g., odikai_drake -> drake)
    // For Odikai playbooks, the format is odikai_{type}
    if (playbookId.startsWith('odikai_')) {
      const playbookType = playbookId.replace('odikai_', '');
      navigate(`/picks?playbook=${playbookType}`);
    } else {
      // For other playbooks, just navigate to picks page
      navigate('/picks');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="page-container">
          <div className="loading-section">
            <div className="loading">
              <div className="loading-spinner"></div>
              <span>Loading top playbooks…</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>

      <div className="page-wrapper">
        <div className="page-container">
          <header className="page-header">
            <div className="header-content">
              <h1>Top playbooks</h1>
              <p className="header-subtitle">
                Odikai's playbooks and public ones from the community, compared on their past results.
              </p>
            </div>
          </header>

          <div className="leaderboard-controls">
            <div className="leaderboard-toggles">
            <div className="filters-row" role="group" aria-label="Filter playbooks">
              <button
                className={`filter-button ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-button ${filter === 'odikai' ? 'active' : ''}`}
                onClick={() => setFilter('odikai')}
              >
                Odikai
              </button>
              <button
                className={`filter-button ${filter === 'user' ? 'active' : ''}`}
                onClick={() => setFilter('user')}
              >
                Community
              </button>
            </div>
            <div className="filters-row" role="group" aria-label="Show accuracy and return as">
              {STAT_MODES.map((mode) => (
                <button
                  key={mode.value}
                  className={`filter-button ${statMode === mode.value ? 'active' : ''}`}
                  aria-pressed={statMode === mode.value}
                  onClick={() => setStatMode(mode.value)}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            </div>
            {filteredAndSortedPlaybooks.length > 0 && (
              <div className="sort-controls-row">
                <span>Sort by</span>
                <div className="sort-dropdown" ref={sortDropdownRef}>
                  <button
                    type="button"
                    className={`sort-select-trigger ${sortDropdownOpen ? 'open' : ''}`}
                    onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                    aria-haspopup="listbox"
                    aria-expanded={sortDropdownOpen}
                  >
                    <span>{SORT_OPTIONS.find(o => o.value === sortBy)?.label ?? 'Sort by'}</span>
                    <ExpandMoreIcon className="sort-select-chevron" sx={{ fontSize: 20 }} />
                  </button>
                  {sortDropdownOpen && (
                    <div className="sort-dropdown-panel" role="listbox">
                      {SORT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          role="option"
                          aria-selected={sortBy === option.value}
                          className={`sort-dropdown-option ${sortBy === option.value ? 'selected' : ''}`}
                          onClick={() => {
                            setSortBy(option.value);
                            setSortDirection('desc');
                            setSortDropdownOpen(false);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="sort-direction-btn"
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  title={sortDirection === 'desc' ? 'High to low (click for low to high)' : 'Low to high (click for high to low)'}
                >
                  {sortDirection === 'desc' ? <ArrowDownwardIcon sx={{ fontSize: 18 }} /> : <ArrowUpwardIcon sx={{ fontSize: 18 }} />}
                  {sortDirection === 'desc' ? 'High to low' : 'Low to high'}
                </button>
              </div>
            )}
          </div>


          {filteredAndSortedPlaybooks.length === 0 ? (
            <div className="empty-state">
              <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.6 }} />
              <p>No public playbooks yet. Check back after the next event, or build your own.</p>
            </div>
          ) : (
            <>
            <div className="playbooks-table-container table-view">
              <table className="playbooks-table">
                <thead>
                  <tr>
                    <th 
                      className={`sortable ${sortBy === 'name' ? 'active' : ''}`}
                      onClick={() => handleSort('name')}
                    >
                      Title
                      {sortBy === 'name' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                        </span>
                      )}
                    </th>
                    <th>Creator</th>
                    <th 
                      className={`sortable ${sortBy === 'avg_accuracy' ? 'active' : ''}`}
                      onClick={() => handleSort('avg_accuracy')}
                    >
                      {currentStatMode.short} accuracy
                      {sortBy === 'avg_accuracy' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                        </span>
                      )}
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'avg_returns' ? 'active' : ''}`}
                      onClick={() => handleSort('avg_returns')}
                    >
                      {currentStatMode.short} return
                      {sortBy === 'avg_returns' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                        </span>
                      )}
                    </th>
                    <th
                      className={`sortable ${sortBy === 'profit_chance' ? 'active' : ''}`}
                      onClick={() => handleSort('profit_chance')}
                      title="Share of past events where the playbook made money"
                    >
                      Profit chance
                      {sortBy === 'profit_chance' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                        </span>
                      )}
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'last_event_return' ? 'active' : ''}`}
                      onClick={() => handleSort('last_event_return')}
                    >
                      Prev return
                      {sortBy === 'last_event_return' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                        </span>
                      )}
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'last_event_accuracy' ? 'active' : ''}`}
                      onClick={() => handleSort('last_event_accuracy')}
                    >
                      Prev accuracy
                      {sortBy === 'last_event_accuracy' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                        </span>
                      )}
                    </th>
                    <th 
                      className={`sortable ${sortBy === 'current_win_streak' ? 'active' : ''}`}
                      onClick={() => handleSort('current_win_streak')}
                    >
                      Streak
                      {sortBy === 'current_win_streak' && (
                        <span className="sort-icon">
                          {sortDirection === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />}
                        </span>
                      )}
                    </th>
                    <th className="action-cell"><span className="visually-hidden">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedPlaybooks.map((playbook) => {
                    const accuracy = eventStat(playbook, 'accuracy', statMode);
                    const returns = eventStat(playbook, 'returns', statMode);
                    const chance = profitChance(playbook);
                    return (
                    <tr key={playbook.id}>
                      <td>
                        <span className="playbook-title">{playbook.name || 'Untitled Playbook'}</span>
                        {untracked(playbook, accuracy, returns) && <span className="playbook-subnote">Not tracked yet</span>}
                      </td>
                      <td>
                        <div className="creator-cell">
                          <span className={`creator-badge ${playbook.creator_type || 'user'}`}>
                            {playbook.creator_type === 'odikai' && 'Odikai'}
                            {playbook.creator_type === 'user' && (playbook.creator_name || 'User')}
                            {playbook.creator_type === 'special' && 'Featured'}
                            {!playbook.creator_type && (playbook.creator_name || 'User')}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span className={`stat-value ${toneAt(accuracy, 0.5)}`}>
                          {formatPercent(accuracy)}
                        </span>
                      </td>
                      <td>
                        <span className={`stat-value ${toneAt(returnsToCentered(returns), 0)}`}>
                          {formatReturn(returns)}
                        </span>
                      </td>
                      <td>
                        <span className={`stat-value ${toneAt(chance?.share, 0.5)}`} title={chanceDetail(chance)}>
                          {formatChance(chance)}
                        </span>
                      </td>
                      <td>
                        <span className={`stat-value ${toneAt(returnsToCentered(playbook.last_event_return), 0)}`}>
                          {formatReturn(playbook.last_event_return)}
                        </span>
                      </td>
                      <td>
                        <span className={`stat-value ${toneAt(playbook.accuracy_pct_last_event, 0.5)}`}>
                          {formatPercent(playbook.accuracy_pct_last_event)}
                        </span>
                      </td>
                      <td>
                        <span className={`stat-value ${playbook.current_win_streak == null ? 'muted' : ''}`}>
                          {playbook.current_win_streak !== undefined && playbook.current_win_streak !== null
                            ? `${playbook.current_win_streak} ${playbook.current_win_streak === 1 ? 'win' : 'wins'}`
                            : '—'}
                        </span>
                      </td>
                      <td className="action-cell">
                        <div className="action-buttons">
                          <button 
                            className="view-button"
                            onClick={() => handleViewPlaybook(playbook.id)}
                          >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                            View
                          </button>
                          <button 
                            className="picks-button"
                            onClick={() => handleViewPicks(playbook.id)}
                          >
                            <BarChartIcon sx={{ fontSize: 16 }} />
                            Picks
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="playbooks-card-grid card-view">
              {filteredAndSortedPlaybooks.map((playbook) => {
                const accuracy = eventStat(playbook, 'accuracy', statMode);
                const returns = eventStat(playbook, 'returns', statMode);
                const chance = profitChance(playbook);
                return (
                <div className="playbook-row-card" key={playbook.id}>
                  <div className="playbook-row-card-header">
                    <div className="playbook-row-card-header-left">
                      <h3 className="playbook-row-card-title">{playbook.name || 'Untitled Playbook'}</h3>
                      {untracked(playbook, accuracy, returns) && <span className="playbook-subnote">Not tracked yet</span>}
                    </div>
                    <div className="playbook-row-card-header-right">
                      <span className={`creator-badge ${playbook.creator_type || 'user'}`}>
                        {playbook.creator_type === 'odikai' && 'Odikai'}
                        {playbook.creator_type === 'user' && (playbook.creator_name || 'User')}
                        {playbook.creator_type === 'special' && 'Featured'}
                        {!playbook.creator_type && (playbook.creator_name || 'User')}
                      </span>
                      <button
                        className="view-button"
                        onClick={() => handleViewPlaybook(playbook.id)}
                      >
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                        View
                      </button>
                      <button
                        className="picks-button"
                        onClick={() => handleViewPicks(playbook.id)}
                      >
                        <BarChartIcon sx={{ fontSize: 16 }} />
                        Picks
                      </button>
                    </div>
                  </div>
                  <div className="playbook-row-card-stats">
                    <div className="playbook-row-card-stat">
                      <span className="playbook-row-card-stat-label">{currentStatMode.label} accuracy</span>
                      <span className={`playbook-row-card-stat-value stat-value ${toneAt(accuracy, 0.5)}`}>
                        {formatPercent(accuracy)}
                      </span>
                    </div>
                    <div className="playbook-row-card-stat">
                      <span className="playbook-row-card-stat-label">{currentStatMode.label} return</span>
                      <span className={`playbook-row-card-stat-value stat-value ${toneAt(returnsToCentered(returns), 0)}`}>
                        {formatReturn(returns)}
                      </span>
                    </div>
                    <div className="playbook-row-card-stat">
                      <span className="playbook-row-card-stat-label">Chance of profit</span>
                      <span className={`playbook-row-card-stat-value stat-value ${toneAt(chance?.share, 0.5)}`} title={chanceDetail(chance)}>
                        {formatChance(chance)}
                      </span>
                    </div>
                    <div className="playbook-row-card-stat">
                      <span className="playbook-row-card-stat-label">Last event return</span>
                      <span className={`playbook-row-card-stat-value stat-value ${toneAt(returnsToCentered(playbook.last_event_return), 0)}`}>
                        {formatReturn(playbook.last_event_return)}
                      </span>
                    </div>
                    <div className="playbook-row-card-stat">
                      <span className="playbook-row-card-stat-label">Last event accuracy</span>
                      <span className={`playbook-row-card-stat-value stat-value ${toneAt(playbook.accuracy_pct_last_event, 0.5)}`}>
                        {formatPercent(playbook.accuracy_pct_last_event)}
                      </span>
                    </div>
                    <div className="playbook-row-card-stat">
                      <span className="playbook-row-card-stat-label">Win streak</span>
                      <span className={`playbook-row-card-stat-value ${playbook.current_win_streak == null ? 'muted' : ''}`}>
                        {playbook.current_win_streak !== undefined && playbook.current_win_streak !== null
                          ? `${playbook.current_win_streak} ${playbook.current_win_streak === 1 ? 'win' : 'wins'}`
                          : '—'}
                      </span>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

